import { Component, inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  NbAlertModule,
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbListModule,
  NbTagModule
} from '@nebular/theme';
import { OperationsService } from 'src/app/pages/operations/operations.service';
import { OperationNotification, OperationTask } from 'shared';
import { map, Observable, take, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-operations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    NbLayoutModule,
    NbCardModule,
    NbTagModule,
    NbButtonModule,
    NbInputModule,
    NbAlertModule,
    NbIconModule,
    NbListModule
  ],
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss']
})
export class OperationsComponent implements OnInit, OnDestroy {
  opService = inject(OperationsService);
  dialogService = inject(NbDialogService);
  activeRoute = inject(ActivatedRoute);

  showScreensaver = false;
  view: 'ondeck' | string = '';
  notifications$: Observable<OperationNotification[]>;
  tasks$: Observable<OperationTask[]>;

  taskAssignee = '';
  loading = false;

  completedStats = new Map<string, OperationTask[]>();

  pendingCount = 0;

  currentAnimationClass = ''; // Stores the CSS class name to be applied (empty when inactive)
  private mainIntervalTimer: any; // Controls the overall 60-second cycle
  private deactivationTimeout: any; // Controls when to turn off the screensaver after 5 seconds

  // --- Configuration ---
  animationIntervalMs = 3 * 60 * 1000; // The total cycle duration (3 min)
  screensaverActiveDurationMs = 10 * 1000; // How long the screensaver animation plays (10 seconds)

  // --- Pool of CSS Animation Classes ---
  // These will be applied one at a time for 5 seconds
  animationClasses: string[] = [
    // 'screensaver-state-diagonal-pan',
    // 'screensaver-state-skew-pan',
    // 'screensaver-state-brightness-pulse',
    // 'screensaver-state-shadow-pulse',
    'screensaver-state-zoom-pulse',
    'screensaver-state-rotate', // Added new state
    'screensaver-state-opacity-flicker' // Added another new state
    // ... and so on
  ];

  private currentClassIndex = 0; // Keeps track of which class to apply next

  ngOnInit(): void {
    // Start the animation cycling when the component is initialized
    this.startScreensaverCycle();

    this.view = this.activeRoute.snapshot.queryParamMap.get('view') || '';

    this.notifications$ = this.opService.getOperationsNotifications();
    this.tasks$ = this.opService.getOperationsTasks().pipe(
      tap((tasks) => {
        this.completedStats.clear();
        this.pendingCount = 0;
        tasks.forEach((task) => {
          if (task.status === 'completed') {
            const arr = this.completedStats.get(task.assignee) || [];
            arr.push(task);
            this.completedStats.set(task.assignee, arr);
          }
          if (task.status === 'pending') {
            this.pendingCount++;
          }
        });
      }),
      map((list) => {
        list = list.filter((task) => {
          return task.status !== 'completed';
        });
        return list;
      })
    );
  }

  ngOnDestroy(): void {
    // Clean up all timers to prevent memory leaks when the component is destroyed
    this.safeClearInterval(this.mainIntervalTimer);
    this.safeClearTimeout(this.deactivationTimeout);
  }

  async completeTask(dialog, task: OperationTask) {
    this.dialogService
      .open(dialog, {
        hasBackdrop: true,
        hasScroll: true,
        autoFocus: false,
        closeOnBackdropClick: true,
        closeOnEsc: true
      })
      .onClose.pipe(take(1))
      .subscribe(async (res) => {
        if (res === true) {
          // ack - push updatee
          task.assignee = this.taskAssignee;
          task.status = 'completed';
          await this.opService.updateOperationsTask(task);
          this.taskAssignee = '';
        }
      });
  }

  viewCompletedTaskStats(taskList: OperationTask[], person: string, modal?: TemplateRef<any>) {
    if (modal) {
      this.dialogService.open(modal, {
        context: {
          taskList,
          person
        }
      });
    }
  }

  private startScreensaverCycle(): void {
    // Trigger the first animation immediately, without waiting for the first interval
    this.activateAndDeactivateAnimation();

    // Set up the main interval to trigger animations every `animationIntervalMs`
    this.mainIntervalTimer = setInterval(() => {
      this.activateAndDeactivateAnimation();
    }, this.animationIntervalMs);
  }

  private activateAndDeactivateAnimation(): void {
    // 1. Apply the next animation class
    this.currentAnimationClass = this.animationClasses[this.currentClassIndex];
    // Move to the next index, looping back to the start
    this.currentClassIndex = (this.currentClassIndex + 1) % this.animationClasses.length;

    // 2. Set a timeout to remove the class after `screensaverActiveDurationMs`
    // Clear any previous deactivation timeout to ensure correct timing if activated quickly
    this.safeClearTimeout(this.deactivationTimeout);
    this.deactivationTimeout = setTimeout(() => {
      this.currentAnimationClass = ''; // Remove the class to stop the animation
    }, this.screensaverActiveDurationMs);
  }

  // --- Helper functions for clearing timers safely ---
  private safeClearInterval(timerId: any): void {
    if (timerId) {
      clearInterval(timerId);
    }
  }

  private safeClearTimeout(timerId: any): void {
    if (timerId) {
      clearTimeout(timerId);
    }
  }
}
